import { OrganizationType } from "../types/organization.type";
import { orgScopedPermission } from "./jwt";
import { ORG_ADMIN_ROLES, USER_MANAGEMENT_ROLES } from "./legacy-roles";
import type { LegacyRole } from "./types";

export type ZeroNavAccessType = "orgScoped" | "orgMembership";

/** Collapsible sidebar sections that nav items are grouped under. */
export type ZeroNavGroup = "Overview" | "Monitoring" | "Management";

export interface ZeroNavItem {
  text: string;
  path: string;
  group: ZeroNavGroup;
  allowedTypes: OrganizationType[];
  accessType: ZeroNavAccessType;
  entity?: string;
  action?: string;
  legacyRoles?: readonly LegacyRole[];
}

const ALL_ORG_TYPES = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
];

/** Zero sidebar catalog — permission + org-type gating. */
export const ZERO_NAV_CATALOG: ZeroNavItem[] = [
  {
    text: "Dashboard",
    path: "/",
    group: "Overview",
    allowedTypes: ALL_ORG_TYPES,
    accessType: "orgScoped",
    entity: "dashboard",
    action: "read",
  },
  {
    text: "Report",
    path: "/report",
    group: "Overview",
    allowedTypes: ALL_ORG_TYPES,
    accessType: "orgScoped",
    entity: "dashboard",
    action: "read",
  },
  {
    text: "Branch analysis",
    path: "/branch-analysis",
    group: "Overview",
    allowedTypes: [OrganizationType.WASTE_OUTLET],
    accessType: "orgScoped",
    entity: "dashboard",
    action: "read",
  },
  {
    text: "Organizations",
    path: "/organizations",
    group: "Management",
    allowedTypes: ALL_ORG_TYPES,
    accessType: "orgMembership",
  },
  {
    text: "Users",
    path: "/users",
    group: "Management",
    allowedTypes: ALL_ORG_TYPES,
    accessType: "orgScoped",
    entity: "user",
    action: "list",
    legacyRoles: USER_MANAGEMENT_ROLES,
  },
  {
    text: "Approvals",
    path: "/approvals",
    group: "Management",
    allowedTypes: ALL_ORG_TYPES,
    accessType: "orgScoped",
    entity: "approval",
    action: "list",
    legacyRoles: ORG_ADMIN_ROLES,
  },
];

export function resolveZeroNavPermission(
  item: ZeroNavItem,
  orgId: string | null,
): string | null {
  if (item.accessType === "orgMembership" || !orgId || !item.entity || !item.action) {
    return null;
  }
  return orgScopedPermission(orgId, item.entity, item.action);
}

export const PERM_DASHBOARD_READ = (orgId: string) =>
  orgScopedPermission(orgId, "dashboard", "read");
export const PERM_ORG_READ = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "read");
export const PERM_ORG_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "write");
export const PERM_UPLOAD_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "upload", "create");
export const PERM_USER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "user", "list");
export const PERM_USER_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "user", "write");
export const PERM_ROLE_ASSIGN = (orgId: string) =>
  orgScopedPermission(orgId, "role", "assign");
export const PERM_USER_DELETE = "recyclee.admin.user.delete";

export const PERM_APPROVAL_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "approval", "list");
export const PERM_APPROVAL_READ = (orgId: string) =>
  orgScopedPermission(orgId, "approval", "read");
export const PERM_APPROVAL_SUBMIT = (orgId: string) =>
  orgScopedPermission(orgId, "approval", "submit");
export const PERM_APPROVAL_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "approval", "write");
export const PERM_ASSIGNMENT_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "assignment", "list");
